"use client";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface PortalTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  placement?: "top" | "right" | "bottom" | "left";
  forceOpen?: boolean; // Add this prop
  className?: string; // Optional className for styling
}

export function PortalTooltip({
  children,
  content,
  placement = "right",
  forceOpen = false, // Add this prop
  className = "", // Optional className for styling
}: PortalTooltipProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({
    position: "fixed",
    zIndex: 9999,
    opacity: 0,
    pointerEvents: "none",
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Tooltip is visible when either hovered or forced open
  const isVisible = isHovered || forceOpen;

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const updatePosition = () => {
        if (!triggerRef.current || !tooltipRef.current) return;

        const triggerRect = triggerRef.current!.getBoundingClientRect();
        const tooltipRect = tooltipRef.current!.getBoundingClientRect();
        const style: React.CSSProperties = {
          position: "fixed",
          zIndex: 9999,
          opacity: 1,
          pointerEvents: "auto",
        };

        let left, top;

        switch (placement) {
          case "top":
            left =
              triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
            top = triggerRect.top - tooltipRect.height - 8;
            break;
          case "bottom":
            left =
              triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
            top = triggerRect.bottom + 8;
            break;
          case "left":
            left = triggerRect.left - tooltipRect.width - 4;
            top =
              triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
            break;
          case "right":
          default:
            left = triggerRect.right + 4;
            top =
              triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
            break;
        }

        // Adjust for viewport edges
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const margin = 8; // Minimum distance from viewport edge

        if (left < margin) left = margin;
        if (left + tooltipRect.width > viewportWidth - margin)
          left = viewportWidth - tooltipRect.width - margin;
        if (top < margin) top = margin;
        if (top + tooltipRect.height > viewportHeight - margin)
          top = viewportHeight - tooltipRect.height - margin;

        style.left = `${left}px`;
        style.top = `${top}px`;

        setTooltipStyle(style);
        setIsPositioned(true);
      };

      // Initial position update
      updatePosition();

      // Update position after a short delay to ensure proper rendering
      const timeoutId = setTimeout(updatePosition, 50);

      // Update position on scroll and resize
      window.addEventListener("scroll", updatePosition);
      window.addEventListener("resize", updatePosition);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("scroll", updatePosition);
        window.removeEventListener("resize", updatePosition);
      };
    } else if (!isVisible) {
      // Reset positioned state when hiding
      setIsPositioned(false);
    }
  }, [isVisible, placement]); // Add isVisible to dependencies

  return (
    <>
      <div
        className={className}
        ref={triggerRef}
        onClick={() => {
          setIsHovered(false);
          setIsPositioned(false);
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setIsPositioned(false);
        }}
      >
        {children}
      </div>
      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            style={tooltipStyle}
            className={`transition-opacity duration-200 ${isPositioned ? "opacity-100" : "opacity-0"}`}
          >
            {content}
          </div>,
          triggerRef.current?.closest("dialog") ?? document.body
        )}
    </>
  );
}
